import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Center,
  Select,
  Table,
  ActionIcon,
  Modal,
  Flex,
  Pagination,
  SegmentedControl,
  rem,
  LoadingOverlay,
  Text,
  Divider,
} from "@mantine/core";
import {
  IconCopy,
  IconQrcode,
  IconList,
  IconSearch,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { QrCodeGenerator } from "@components/shared";
import { rideChallengePath } from "@components/RideChallenge/helpers";

const pageSize = 25;

export default function EffortLocationJoinList({
  effortId,
  isRideChallenge = false,
  locationId = null,
  isVehicleDataEffort = false,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("search");
  const [codes, setCodes] = useState([]);
  const [selected, setSelected] = useState("");
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(codes.slice(0, pageSize));
  const pageCount = Math.round(codes.length / pageSize);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize;
    setRecords(codes.slice(from, to));
  }, [page, JSON.stringify(codes)]);

  function fetchData() {
    setLoading(true);
    axios
      .post(`/efforts/${effortId}/get-url-codes-for-qr/`, {
        campaign_effort_id: effortId,
      })
      .then(({ data }) => {
        const res = data.response;
        if (locationId) {
          const matchedLocation = res.find((f) => f.id === locationId);
          if (matchedLocation) {
            setSelected(matchedLocation.code);
          }
        }
        setCodes(data.response);
        setLoading(false);
      })
      .catch((err) => {
        setCodes([]);
        setLoading(false);
      });
  }

  const options = codes.map((code) => ({
    value: code.code,
    label: code.name,
  }));

  const selectedCode = codes.find((f) => f.code === selected);

  const codeUrl = !selected
    ? null
    : isRideChallenge || isVehicleDataEffort
    ? `${rideChallengePath}/${selected}`
    : `${import.meta.env.VITE_DOMAIN}/entry/${selected}`;

  return (
    <div>
      <div>
        <LoadingOverlay
          visible={loading}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 1 }}
        />
      </div>
      {!locationId && (
        <SegmentedControl
          mb="lg"
          size="sm"
          value={mode}
          onChange={(e) => setMode(e)}
          data={[
            {
              label: (
                <Center style={{ gap: 10 }}>
                  <IconSearch style={{ width: rem(16), height: rem(16) }} />
                  <span>{t("mixer:effort:search_for_a_location")}</span>
                </Center>
              ),
              value: "search",
            },
            {
              label: (
                <Center style={{ gap: 10 }}>
                  <IconList style={{ width: rem(16), height: rem(16) }} />
                  <span>{t("mixer:effort:view_all_locations")}</span>
                </Center>
              ),
              value: "table",
            },
          ]}
        />
      )}
      {mode === "search" && (
        <>
          {!locationId && (
            <Select
              data={options}
              searchable
              label={t("mixer:location_singular")}
              placeholder={t("common:search")}
              value={selected}
              onChange={(e) => setSelected(e)}
              mb={codeUrl ? "lg" : "0"}
            />
          )}
          {codeUrl && (
            <>
              {selectedCode && selectedCode.dashboard_code && (
                <Text fw={600}>{t("mixer:ride_challenge_singular")}</Text>
              )}
              <QrCodeGenerator text={codeUrl} />
              {selectedCode && selectedCode.dashboard_code && (
                <>
                  <Divider mt="lg" mb="lg" />
                  <Text fw={600}>{t("common:dashboard")}</Text>
                  <QrCodeGenerator
                    text={getDashboardCode(selectedCode.dashboard_code)}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
      {mode === "table" && (
        <>
          <Table striped highlightOnHover>
            <Table.Tbody>
              {records.map((code) => (
                <Table.Tr key={code.id}>
                  <Table.Td>{code.name}</Table.Td>
                  <Table.Td>
                    <Flex align="center" gap="xs">
                      <div>
                        {code.dashboard_code && (
                          <span>
                            <b>{t("mixer:ride_challenge_singular")}</b>
                          </span>
                        )}
                        {/* <span>
                          {isRideChallenge
                            ? `${rideChallengePath}/${code.code}`
                            : `${import.meta.env.VITE_DOMAIN}/entry/${
                                code.code
                              }`}
                        </span> */}
                      </div>
                      <Flex gap="xs" align="center">
                        <ActionIcon
                          size="sm"
                          color="gray"
                          variant="light"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              isRideChallenge || isVehicleDataEffort
                                ? `${rideChallengePath}/${code.code}`
                                : `${import.meta.env.VITE_DOMAIN}/entry/${
                                    code.code
                                  }`
                            );
                            toast.success(t("common:copied") + "!");
                          }}
                        >
                          <IconCopy style={{ width: "70%", height: "70%" }} />
                        </ActionIcon>
                        <CodeModal
                          codeUrl={
                            isRideChallenge || isVehicleDataEffort
                              ? `${rideChallengePath}/${code.code}`
                              : `${import.meta.env.VITE_DOMAIN}/entry/${
                                  code.code
                                }`
                          }
                          title={code.name}
                        />
                      </Flex>
                    </Flex>
                  </Table.Td>
                  {code.dashboard_code && (
                    <Table.Td>
                      <Flex align="center" gap="xs">
                        <span>
                          <b>{t("common:dashboard")}</b>
                        </span>
                        {/* <span>{getDashboardCode(code.dashboard_code)}</span> */}
                        <ActionIcon
                          size="sm"
                          color="gray"
                          variant="light"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              getDashboardCode(code.dashboard_code)
                            );
                            toast.success("Copied!");
                          }}
                        >
                          <IconCopy style={{ width: "70%", height: "70%" }} />
                        </ActionIcon>
                        <CodeModal
                          codeUrl={getDashboardCode(code.dashboard_code)}
                          title={`${code.name} ${t("common:dashboard")}`}
                        />
                      </Flex>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Pagination
            mt="sm"
            value={page}
            onChange={setPage}
            total={pageCount}
          />
        </>
      )}
    </div>
  );
}

const CodeModal = ({ codeUrl, title }) => {
  const [isOpen, setOpen] = useState(false);

  function onClose() {
    setOpen(false);
  }

  return (
    <>
      <ActionIcon
        size="sm"
        color="gray"
        variant="light"
        onClick={() => {
          setOpen(true);
        }}
      >
        <IconQrcode style={{ width: "70%", height: "70%" }} />
      </ActionIcon>
      <Modal title={title} size="lg" opened={isOpen} onClose={onClose}>
        <QrCodeGenerator text={codeUrl} />
      </Modal>
    </>
  );
};

export function getDashboardCode(code) {
  return `${import.meta.env.VITE_DOMAIN}/code/${code}`;
}
